import CtaIcon from '../ui/CtaIcon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../../services/ApiService';
import {
  getPassportOwnerAnalytics,
  getPassportSubjectSettings,
  listPassportSubjectScanTokenBundle,
  revokePassportSubjectScanToken,
  updatePassportSubjectSettings,
  type PassportBuildQr,
  type PassportOwnerAnalytics,
  type PassportScanTokenSummary,
  type PassportSubjectSettings,
} from '../../services/api/passport';
import ConfirmDialog from '../ui/ConfirmDialog';
import { scanTokenStatusLabel, verificationLevelLabel } from './passportCopy';
import PassportStickerStudio from './PassportStickerStudio';
import { normalizePassportStickerSubjectLabel } from '../../utils/passportStickerCanvas';

type Props = {
  vehicleId: number;
  /** Footer label on Passport vinyl (year/make/model + nickname). */
  subjectLabel?: string | null;
  isOwner: boolean;
  /** Opens the Share / QR modal so print tools stay in one place. */
  onOpenShareQr?: () => void;
  /** When true, omit the outer card (Manage build section already frames the content). */
  embedded?: boolean;
  /**
   * Share modal surface: sticker print first, encounter settings after.
   * Hides the “open Share” CTA (already on Share).
   */
  shareSurface?: boolean;
};

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Owner Passport tools for a vehicle.
 *
 * One QR rule: the public build QR is also the Passport sticker. Collectors who
 * scan it in Bilt:Spec log an encounter; a phone camera opens the build page.
 * Separate /passport/s/ minting is retired for vehicles.
 */
const VehiclePassportOwnerPanel: React.FC<Props> = ({
  vehicleId,
  subjectLabel,
  isOwner,
  onOpenShareQr,
  embedded = false,
  shareSurface = false,
}) => {
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [toolsWarning, setToolsWarning] = useState<string | null>(null);
  const loadVersion = useRef(0);
  const [analytics, setAnalytics] = useState<PassportOwnerAnalytics | null>(null);
  const [settings, setSettings] = useState<PassportSubjectSettings | null>(null);
  const [buildQr, setBuildQr] = useState<PassportBuildQr | null>(null);
  const [legacyTokens, setLegacyTokens] = useState<PassportScanTokenSummary[]>([]);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [revokeBusyId, setRevokeBusyId] = useState<number | null>(null);
  const [pendingRevokeId, setPendingRevokeId] = useState<number | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!isOwner || !vehicleId) return;
    const version = ++loadVersion.current;
    setLoadState('loading');
    setError(null);
    setToolsWarning(null);
    setAnalytics(null);
    setSettings(null);
    const current = () => mounted.current && version === loadVersion.current;
    const optionalFailure = () => {
      if (current()) setToolsWarning('Some Passport settings or activity could not load. QR printing is handled separately.');
    };
    // Optional owner tools cannot delay or hide the independently authorized build QR.
    void getPassportOwnerAnalytics(vehicleId).then(value => {
      if (current()) setAnalytics(value);
    }).catch(optionalFailure);
    void getPassportSubjectSettings('vehicle', vehicleId).then(value => {
      if (current()) setSettings(value);
    }).catch(optionalFailure);
    try {
      const tokenBundle = await listPassportSubjectScanTokenBundle('vehicle', vehicleId);
      if (!current()) return;
      setBuildQr(tokenBundle.build_qr ?? null);
      setLegacyTokens(
        (tokenBundle.tokens || []).filter((t) => t.status === 'active' || t.status === 'expired')
      );
      setLoadState('ready');
      void apiService.trackEvent('passport_owner_analytics_viewed', {
        metadata: { vehicle_id: vehicleId },
      });
    } catch (err: unknown) {
      if (!mounted.current || version !== loadVersion.current) return;
      setError(err instanceof Error ? err.message : 'Could not load Passport owner tools');
      setLoadState('error');
    }
  }, [vehicleId, isOwner]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchSetting = async (field: keyof PassportSubjectSettings, value: boolean) => {
    if (!settings) return;
    const previous = settings;
    setSettings({ ...settings, [field]: value });
    setSettingsBusy(true);
    setActionError(null);
    try {
      const next = await updatePassportSubjectSettings('vehicle', vehicleId, { [field]: value });
      setSettings(next);
    } catch (err: unknown) {
      setSettings(previous);
      setActionError(err instanceof Error ? err.message : 'Could not update settings');
    } finally {
      setSettingsBusy(false);
    }
  };

  const handleRevoke = async (tokenId: number) => {
    setRevokeBusyId(tokenId);
    setActionError(null);
    setActionMessage(null);
    try {
      await revokePassportSubjectScanToken({
        subject_type: 'vehicle',
        subject_id: vehicleId,
        token_id: tokenId,
        reason: 'owner_revoked_legacy',
      });
      setLegacyTokens((prev) =>
        prev.map((t) => (t.id === tokenId ? { ...t, status: 'revoked' } : t))
      );
      setActionMessage('Old Passport-only code revoked. Use your build QR going forward.');
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Could not revoke code');
    } finally {
      setRevokeBusyId(null);
      setPendingRevokeId(null);
    }
  };

  if (!isOwner) return null;

  const shellClass = embedded ? 'space-y-8' : 'card mt-6 p-5 space-y-8';
  const loadingClass = embedded ? 'space-y-4' : 'card mt-6 p-5 space-y-4';

  if (loadState === 'loading' || loadState === 'idle') {
    return (
      <div className={loadingClass} aria-busy="true" aria-label="Loading Passport owner tools">
        <div className="h-5 w-40 rounded bg-white/10 animate-pulse" />
        <div className="h-4 w-full max-w-md rounded bg-white/5 animate-pulse" />
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="h-16 rounded bg-white/5 animate-pulse" />
          <div className="h-16 rounded bg-white/5 animate-pulse" />
          <div className="h-16 rounded bg-white/5 animate-pulse" />
        </div>
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div
        className={embedded ? 'space-y-3' : 'card mt-6 p-5 space-y-3'}
        aria-labelledby="passport-owner-error"
      >
        {!embedded ? (
          <h2 id="passport-owner-error" className="heading-section text-sm">
            Passport
          </h2>
        ) : (
          <h2 id="passport-owner-error" className="sr-only">
            Passport
          </h2>
        )}
        <div className="alert-error text-sm" role="alert">
          {error || 'Could not load Passport owner tools.'}
        </div>
        <button type="button" className="btn-secondary min-h-11" onClick={() => void load()}>
          Retry
        </button>
      </div>
    );
  }

  const mix = analytics?.verification_mix ?? [];
  const daily = analytics?.daily_30d ?? [];
  const maxDaily = Math.max(1, ...daily.map((d) => Number(d.unique_users || 0)));
  const pendingRevokeToken = legacyTokens.find((t) => t.id === pendingRevokeId) || null;
  const activeLegacy = legacyTokens.filter((t) => t.status === 'active');
  const stickerFooterLabel =
    normalizePassportStickerSubjectLabel(subjectLabel) ??
    normalizePassportStickerSubjectLabel(buildQr?.sticker_label);

  return (
    <div className={shellClass} aria-labelledby="passport-owner-heading">
      <div>
        <h2
          id="passport-owner-heading"
          className={embedded || shareSurface ? 'sr-only' : 'heading-section text-sm'}
        >
          Build QR and Passport
        </h2>
        <p className={`text-muted text-sm max-w-2xl ${embedded || shareSurface ? '' : 'mt-1'}`}>
          {shareSurface
            ? 'One build QR. Print it as a Passport sticker below. Phone cameras open the build page; collectors who scan in Bilt:Spec log an encounter when you allow it.'
            : 'One QR for this build. Phone cameras open the build page. Collectors who scan it in Bilt:Spec add a Passport encounter. You do not create a second Passport code.'}
        </p>
      </div>

      {actionError ? (
        <div className="alert-error text-sm" role="alert">
          {actionError}
        </div>
      ) : null}
      {actionMessage ? (
        <p className="text-sm text-emerald-300" role="status">
          {actionMessage}
        </p>
      ) : null}

      {!shareSurface ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
          <h3 className="text-xs uppercase tracking-wide text-meta">How it works</h3>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li>
              Print the build QR from Share <CtaIcon direction="next" /> QR &amp; sticker.
            </li>
            <li>
              Scanning records a QR encounter when collection is on. Physical presence requires
              stronger verification.
            </li>
            <li>Gate entry codes at events are different and never live here.</li>
          </ul>
          {onOpenShareQr ? (
            <button type="button" className="btn-primary min-h-11" onClick={onOpenShareQr}>
              Open QR &amp; sticker
            </button>
          ) : null}
        </div>
      ) : null}

      {toolsWarning ? <div role="status" className="rounded-xl border border-amber-500/25 p-4 space-y-2 text-sm text-amber-100">
        <p>{toolsWarning}</p>
        <button type="button" className="btn-secondary" onClick={() => void load()}>Retry settings and activity</button>
      </div> : null}

      {/* Print first on Share; settings first elsewhere */}
      {shareSurface ? (
        <>
          {buildQr?.qr_svg_inverted || buildQr?.qr_svg ? (
            <div className="space-y-4">
              <PassportStickerStudio
                title="Build sticker"
                subjectLabel={stickerFooterLabel}
                support="This is the only printable build QR. 3 in or 2 in portrait vinyl with the Bilt:Spec lockup under the code. Same code phones and Bilt:Spec Scan use."
                qrSvg={buildQr.qr_svg}
                qrSvgInverted={buildQr.qr_svg_inverted}
                scanUrl={buildQr.scan_url || null}
              />
            </div>
          ) : (
            <div className="space-y-2"><p className="text-sm text-zinc-400" role="status">
              {buildQr?.scan_url ? 'The QR image is unavailable. Retry to generate it again.' : 'Add a public build URL in your build settings to make its QR available.'}
            </p><button type="button" className="btn-secondary" onClick={() => void load()}>Retry QR</button></div>
          )}
        </>
      ) : null}

      {/* Settings */}
      {settings ? <div className="space-y-3">
        <h3 className="text-xs uppercase tracking-wide text-meta">Encounter settings</h3>
        {(
          [
            {
              key: 'collection_enabled' as const,
              label: 'Allow Passport encounters from this build QR',
            },
            {
              key: 'public_count_enabled' as const,
              label: 'Show how many people have collected this build',
            },
            {
              key: 'notifications_enabled' as const,
              label: 'Notify me about Passport milestones',
            },
          ] as const
        ).map(({ key, label }) => (
          <label key={key} className="flex items-start gap-3 cursor-pointer min-h-11">
            <input
              type="checkbox"
              className="checkbox-control mt-1"
              checked={Boolean(settings?.[key])}
              disabled={settingsBusy || !settings}
              onChange={(e) => void patchSetting(key, e.target.checked)}
            />
            <span className="text-sm text-white leading-snug">{label}</span>
          </label>
        ))}
      </div> : null}

      {!shareSurface && (buildQr?.qr_svg_inverted || buildQr?.qr_svg) ? (
        <div className="space-y-4">
          <PassportStickerStudio
            title="Build sticker"
            subjectLabel={stickerFooterLabel}
            support="Same build QR as Share. Portrait vinyl in 3 in or 2 in widths."
            qrSvg={buildQr.qr_svg}
            qrSvgInverted={buildQr.qr_svg_inverted}
            scanUrl={buildQr.scan_url || null}
          />
        </div>
      ) : null}

      {/* Legacy separate Passport tokens */}
      {activeLegacy.length > 0 ? (
        <div className="space-y-3 rounded-xl border border-amber-500/25 bg-amber-500/5 p-4">
          <h3 className="text-xs uppercase tracking-wide text-amber-200">
            Older Passport-only codes
          </h3>
          <p className="text-sm text-amber-100/90">
            These were created before build QR and Passport were unified. Revoke stickers that still
            use them so everyone scans your build QR instead.
          </p>
          <ul className="space-y-2" aria-label="Legacy Passport scan codes">
            {activeLegacy.map((token) => (
              <li
                key={token.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 px-3 py-2"
              >
                <div className="min-w-0 text-sm">
                  <span className="font-mono text-xs text-meta">{token.token_fingerprint}</span>
                  <span className="mx-2 text-meta">·</span>
                  <span className="badge">{scanTokenStatusLabel(token.status)}</span>
                </div>
                <button
                  type="button"
                  className="btn-danger min-h-11"
                  disabled={revokeBusyId === token.id}
                  onClick={() => setPendingRevokeId(token.id)}
                  aria-label={`Revoke legacy Passport QR ${token.token_fingerprint}`}
                >
                  {revokeBusyId === token.id ? 'Revoking…' : 'Revoke'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Analytics */}
      {analytics ? <div className="space-y-4">
        <div>
          <h3 className="text-xs uppercase tracking-wide text-meta">Seen in person</h3>
          <p className="text-muted text-sm mt-1">
            Unique collectors only. Pending, rejected, and your own scans stay private.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-meta">Basic QR</p>
            <p className="text-2xl text-white tabular-nums">
              {analytics?.qr_encounters_basic ?? 0}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-meta">Stronger verification</p>
            <p className="text-2xl text-white tabular-nums">
              {analytics?.seen_in_person_strong ?? 0}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-meta">Held or flagged</p>
            <p className="text-2xl text-white tabular-nums">{analytics?.suspicious_or_held ?? 0}</p>
          </div>
        </div>

        {mix.length > 0 ? (
          <ul className="space-y-1 text-sm text-zinc-300">
            {mix.map((m) => (
              <li key={m.verification_level}>
                {verificationLevelLabel(m.verification_level)}: {m.unique_users} people ({m.total}{' '}
                scans)
              </li>
            ))}
          </ul>
        ) : null}

        {daily.length > 0 ? (
          <div aria-label="Unique collectors over the last 30 days">
            <p className="text-xs uppercase tracking-wide text-meta">Last 30 days</p>
            <div
              className="mt-2 flex h-16 items-end gap-0.5"
              role="img"
              aria-label="Daily unique collectors chart"
            >
              {daily.map((d) => (
                <div
                  key={d.date}
                  title={`${d.date}: ${d.unique_users}`}
                  className="flex-1 rounded-sm bg-orange-500/70"
                  style={{ height: `${Math.max(8, (Number(d.unique_users) / maxDaily) * 100)}%` }}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div> : null}

      <ConfirmDialog
        isOpen={pendingRevokeId !== null}
        title="Revoke this old Passport code?"
        description={
          <>
            <p>Stickers printed with this older code stop logging encounters.</p>
            <p className="mt-2">Your build QR from Share keeps working.</p>
          </>
        }
        confirmLabel="Revoke code"
        loadingLabel="Revoking…"
        destructive
        loading={revokeBusyId !== null}
        onConfirm={() => (pendingRevokeToken ? handleRevoke(pendingRevokeToken.id) : undefined)}
        onCancel={() => setPendingRevokeId(null)}
      />
    </div>
  );
};

export default VehiclePassportOwnerPanel;
